import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { supabase } from "../services/supabase";
import { siteConfig } from "../config/siteConfig";

const AuthContext = createContext();

// Logger de depuración: silencioso en producción para no exponer PII
// (email, userId, teléfono) en la consola del navegador del cliente.
const isDev = process.env.NODE_ENV !== "production";
const debugLog = (...args) => {
  if (isDev) console.log(...args);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshProfile = async () => {
    if (!user?.id) return null;

    debugLog("🔄 Refrescando datos del perfil...");
    const updatedProfile = await getProfile(user.id);

    if (updatedProfile) {
      setProfile(updatedProfile);
      setIsAuthenticated(true);
    }
    return updatedProfile;
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://auth-hub.bremondsas.workers.dev/?next=" + encodeURIComponent(window.location.origin + "/update-password"),
    });
    if (error) throw error;
    return data;
  };

  const getProfile = async (userId) => {
    try {
      debugLog("1. Entrando a getProfile...");

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("4. Error específico de consulta:", error);
        return null;
      }

      debugLog("5. Éxito final:", data);
      return data;
    } catch (err) {
      console.error("💥 Error atrapado en catch:", err);
      return null;
    }
  };

  // useEffect corregido para evitar ejecuciones dobles
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user && isMounted) {
          setUser(session.user);
          // La autenticación depende de la SESIÓN válida, no de que la
          // carga del perfil tenga éxito. Si getProfile() falla por un
          // fallo de red transitorio (devuelve null), un usuario con sesión
          // válida NO debe aparecer como deslogueado ni ser expulsado al
          // landing. El perfil puede recargarse luego con refreshProfile().
          setIsAuthenticated(true);
          let p = await getProfile(session.user.id);
          if (!p) {
            // Primer login OAuth (Google): la fila de profiles puede no existir aun.
            p = await createProfile(session.user.id, session.user.email, {
              name:
                session.user.user_metadata?.full_name ||
                session.user.user_metadata?.name ||
                "",
            });
          }
          if (isMounted) setProfile(p);
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) setLoading(false); // <--- Esto garantiza que el loading se quite siempre
      }
    };

    initialize();

    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      debugLog(`🔄 Evento Auth: ${event}`);

      // ❌ ELIMINADO: No más auto-login en SIGNED_IN
      // if (event === "SIGNED_IN" && session?.user) {
      //   setUser(session.user);
      //   const p = await getProfile(session.user.id);
      //   setProfile(p);
      //   setIsAuthenticated(true);
      // }

      if (event === "SIGNED_OUT") {
        setUser(null);
        setProfile(null);
        setIsAuthenticated(false);
      }
    });

    return () => {
      isMounted = false;
      data?.subscription?.unsubscribe();
    };
    // Solo debe ejecutarse una vez al montar; getProfile/createProfile son
    // estables funcionalmente (cierran sobre supabase/siteConfig, no estado).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // CREAR PERFIL
  // ============================================
  const createProfile = async (userId, email, userData = {}) => {
    try {
      debugLog(`🆕 ===== CREATE PROFILE INICIADO =====`);
      debugLog(`📧 Email: ${email}`);
      debugLog(`🆔 UserId: ${userId}`);
      debugLog(`📱 Teléfono recibido:`, userData.phone);

      // 1. VERIFICAR SI EL PERFIL YA EXISTE
      // Distinguimos «fila ausente» (data null sin error / PGRST116) de un
      // «error real» (red, permisos). Ante un error real NO creamos nada:
      // podríamos machacar un perfil existente (stats/progress/saas a cero).
      const { data: existingProfile, error: checkError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (checkError && checkError.code !== "PGRST116") {
        console.error(`❌ Error verificando perfil existente:`, checkError);
        throw checkError;
      }

      if (existingProfile) {
        debugLog(`✅ El perfil YA EXISTE, no se sobrescribe:`, existingProfile);
        return existingProfile;
      }

      // 2. PREPARAR DATOS DEL PERFIL
      const newProfile = {
        id: userId,
        email: email,
        name: userData.name || email.split("@")[0] || "Usuario",
        phone: userData.phone || "",
        profession: siteConfig.niche.aspirantLabel,
        bio: "",
        avatar_url: null,
        join_date: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        exam_count: 0,
        best_score: 0,
        progress: 0,
        saas: siteConfig.saasKey || null,
      };

      // console.log(`📱 OBJETO 4:`, newProfile);
      // 3. INSERTAR PERFIL
      const { data, error } = await supabase
        .from("profiles")
        .insert([newProfile])
        .select()
        .single();

      if (error) {
        console.error(`❌ Error al insertar perfil:`, error);

        if (error.code === "23505") {
          // Conflicto de clave: la fila ya existe. ignoreDuplicates garantiza
          // que NUNCA se sobrescribe el perfil existente.
          debugLog(`⚠️ Conflicto de clave, upsert sin sobrescribir...`);
          const { data: upsertData, error: upsertError } = await supabase
            .from("profiles")
            .upsert([newProfile], { onConflict: "id", ignoreDuplicates: true })
            .select()
            .maybeSingle();

          if (upsertError) {
            console.error(`❌ Upsert también falló:`, upsertError);
            throw upsertError;
          }

          // Si el duplicado fue ignorado (upsertData null), devolvemos la
          // fila existente tal cual, sin tocarla.
          return upsertData || (await getProfile(userId));
        }
        throw error;
      }

      debugLog(`✅ Perfil creado exitosamente:`, data);
      return data;
    } catch (error) {
      console.error(`💥 Error CRÍTICO en createProfile:`, error);
      return null;
    }
  };

  // ============================================
  // LOGIN - ÚNICA FORMA DE AUTENTICARSE
  // ============================================
  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      let profileData = await getProfile(data.user.id);

      if (!profileData) {
        profileData = await createProfile(
          data.user.id,
          email,
          data.user.user_metadata || {}
        );
      }

      setUser(data.user);
      setProfile(profileData);
      setIsAuthenticated(true);

      return {
        success: true,
        user: data.user,
        profile: profileData,
      };
    } catch (error) {
      console.error("❌ Error en login:", error.message);
      return { success: false, error: error.message };
    } finally {
    }
  };

  // ============================================
  // REGISTRO - SOLO CREA, NO AUTENTICA
  // ============================================
  const register = async (email, password, userData) => {
    try {
      debugLog("\n🔵 ===== REGISTRO INICIADO =====");
      debugLog("📝 Datos:", { email, userData });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone || "",
          },
        },
      });

      if (error) throw error;

      debugLog(`✅ Usuario creado en Auth:`, {
        id: data.user?.id,
        email: data.user?.email,
      });

      if (data.user) {
        // Crear perfil
        debugLog(`🆕 Creando perfil para ${data.user.id}...`);
        const profileData = await createProfile(data.user.id, email, {
          name: userData.name,
          phone: userData.phone || "",
        });

        if (profileData) {
          debugLog(`✅ Perfil creado exitosamente`);

          return {
            success: true,
            user: data.user,
            profile: profileData,
            message: "Rekisteröinti onnistui! Voit nyt kirjautua sisään.",
          };
        }
      }

      await supabase.auth.signOut();

      return {
        success: true,
        user: data.user,
        message: "Rekisteröinti onnistui. Voit nyt kirjautua palveluun.",
      };
    } catch (error) {
      console.error(`❌ Error en registro:`, error);
      return { success: false, error: error.message };
    } finally {
      debugLog(`🏁 ===== REGISTRO FINALIZADO =====\n`);
    }
  };

  // ============================================
  // LOGOUT
  // ============================================
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("❌ Error en logout:", error.message);
      return { success: false, error: error.message };
    }
  };

  // ============================================
  // ACTUALIZAR PERFIL
  // ============================================
  const updateProfile = async (updatedData) => {
    try {
      if (!user) throw new Error("No hay usuario autenticado");

      // ✅ CAMBIO CLAVE: Usamos el objeto spread (...updatedData)
      // para enviar CUALQUIER campo que venga en la llamada
      const { data, error } = await supabase
        .from("profiles")
        .update({
          ...updatedData, // Esto incluye automáticamente read_laws, progress, etc.
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select()
        .single();

      if (error) throw error;

      // Actualizamos el estado global para que React redibuje los componentes
      setProfile(data);

      return {
        success: true,
        profile: data,
        message: "Perfil actualizado correctamente",
      };
    } catch (error) {
      console.error("❌ Error actualizando perfil:", error);
      return { success: false, error: error.message };
    }
  };

  // ============================================
  // VALOR DEL CONTEXTO
  // ============================================
  // Memoizamos el valor del contexto para que su identidad solo cambie
  // cuando cambia el estado de autenticación. Así evitamos re-renders
  // innecesarios en todos los consumidores de useAuth() (y de AppContext,
  // que consume useAuth) en cada render del provider. Los handlers se
  // recapturan en cada recálculo del memo, por lo que siempre cierran sobre
  // el estado actual sin necesidad de envolverlos en useCallback.
  // ============================================
  // LOGIN CON GOOGLE (OAuth via Supabase)
  // ============================================
  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: "https://auth-hub.bremondsas.workers.dev/?next=" + encodeURIComponent(window.location.origin + "/") },
    });
    if (error) throw error;
  };

  const value = useMemo(
    () => ({
      user,
      profile,
      isAuthenticated,
      loading,
      login,
      register,
      signInWithGoogle,
      logout,
      updateProfile,
      refreshProfile,
      resetPassword,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, profile, isAuthenticated, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
